import type { AnalysisRow, CustomerInfo, PrepStory } from "@acto/ambient-context-schema";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { capList } from "./helpers";

/** The customer in context → the schema's CustomerInfo (mirrors the customer info page). */
export function customerToInfo(customer: Customer, locale: string): { customer_info: CustomerInfo } {
  const tags: Record<string, string> = {};
  for (const tag of customer.clientTags ?? []) {
    if (tag.name.hasTranslations() && tag.value) {
      tags[tag.name.getTranslation(locale)] = tag.value;
    }
  }
  return {
    customer_info: {
      external_reference: customer.externalReference,
      name: customer.name,
      street: customer.street,
      postcode: customer.postcode,
      city: customer.city,
      category: customer.industry,
      region: customer.region,
      tags: Object.keys(tags).length > 0 ? tags : undefined,
    },
  };
}

/** Structural mirror of the genui meeting-prep parts, so core mappers don't import fs2 modules. */
export interface PrepStoryPartInput {
  type: string;
  storyKey?: string | null;
  storyHeadline?: string;
  description?: string;
  category?: string;
}

/** Visible meeting-prep parts → schema stories (story cards only, post-removal order). */
export function storyPartsToStories(
  parts: readonly PrepStoryPartInput[] | undefined,
  { limit }: { limit?: number } = {},
): { stories: PrepStory[] } {
  const cards = (parts ?? []).filter((part) => part.type === "storyCard" && part.storyHeadline);
  return {
    stories: capList(cards, limit).map((card) => ({
      story_key: card.storyKey,
      headline: card.storyHeadline as string,
      description: card.description,
      category: card.category,
    })),
  };
}

/** Structural mirror of the fs2 ProductTrendItem / breakdown rows. */
export interface AnalysisRowInput {
  name: string;
  externalReference?: string | null;
  value?: number | null;
  percentageChange?: number | null;
}

/** Rendered analysis breakdown rows → schema rows, in render order. */
export function rowsToAnalysisRows(
  items: readonly AnalysisRowInput[] | undefined,
  { limit }: { limit?: number } = {},
): { visible_rows: AnalysisRow[] } {
  return {
    visible_rows: capList(items ?? [], limit).map((item) => ({
      name: item.name,
      external_reference: item.externalReference,
      value: item.value,
      percentage_change: item.percentageChange,
    })),
  };
}
